import {
  getBenefitCategoryLabel,
  getProductPayrollBenefitDefault,
  getPayrollBenefitSubcategoryOptions,
  getPayrollBenefitSubcategoryLabel,
  PAYROLL_BENEFIT_CATEGORY_OPTIONS,
} from '../../../../services/payroll-benefits-assignment/payroll-benefit-categories';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  Field,
  FieldType,
  MemberAddeditComponent,
  MemberAddeditDialogModel,
} from '../../../brokeragency/member-addedit/member-addedit.component';
import { UiAlertService } from '../../../../services/ui-alert/ui-alert.service';
import {
  VendorProduct,
  VendorProductsService,
} from '../../../../services/vendorproducts/vendorproducts.service';

@Component({
    selector: 'app-vendor-products-product-list',
    templateUrl: './vendor-products-product-list.component.html',
    styleUrls: ['./vendor-products-product-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class VendorProductsProductListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatSort) sort!: MatSort;
  public searchValue = '';
  public tableDataSource = new MatTableDataSource<VendorProduct>();
  public columnNames = [
    'vendorName',
    'vendorSlug',
    'productName',
    'productSlug',
    'metadata',
    'stripeProductId',
    'actions',
  ];
  private readonly metadataPreviewLength = 200;
  private readonly searchDebounceMs = 300;
  private readonly searchValueChanges = new Subject<string>();
  private readonly searchSubscription: Subscription;
  private readonly dialogFields: Field[] = [
    { name: 'id', label: 'ID', fieldType: FieldType.F_STRING, readOnly: true },
    { name: 'vendorName', label: 'Vendor Name', fieldType: FieldType.F_STRING, required: true },
    { name: 'productName', label: 'Product Name', fieldType: FieldType.F_STRING, required: true },
    { name: 'vendorSlug', label: 'Vendor Slug', fieldType: FieldType.F_STRING },
    { name: 'productSlug', label: 'Product Slug', fieldType: FieldType.F_STRING },
    { name: 'stripeProductId', label: 'Stripe Product ID', fieldType: FieldType.F_STRING },
    { name: 'stripeVendor', label: 'Stripe Vendor', fieldType: FieldType.F_STRING },
    {
      name: 'defaultBenefitCategory', label: 'Default benefit category', fieldType: FieldType.F_DROPDOWN,
      values: ['No default:none', ...PAYROLL_BENEFIT_CATEGORY_OPTIONS.map(category => `${getBenefitCategoryLabel(category)}:${category}`)],
      hint: 'Prefills future company product selections. Existing companies keep their assignments.',
    },
    {
      name: 'defaultBenefitSubcategory', label: 'Default benefit subcategory', fieldType: FieldType.F_DROPDOWN,
      valuesResolver: form => ['No subcategory:none', ...getPayrollBenefitSubcategoryOptions(form.get('defaultBenefitCategory')?.value)
        .map(id => `${getPayrollBenefitSubcategoryLabel(form.get('defaultBenefitCategory')?.value, id)}:${id}`)],
    },
    { name: 'metadata', label: 'Metadata JSON', fieldType: FieldType.F_JSON },
    { name: 'feedConfig', label: 'Feed Config JSON', fieldType: FieldType.F_JSON },
  ];

  constructor(
    private readonly vendorProductsService: VendorProductsService,
    private readonly dialog: MatDialog,
    private snackBar: MatSnackBar,
    private uiAlertService: UiAlertService,
  ) {
    this.tableDataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'metadata':
          return this.getMetadataText(item);
        default:
          return ((item as Record<string, any>)[property] || '').toString().toLowerCase();
      }
    };
    this.tableDataSource.filterPredicate = (item, filter) => this.matchesFilter(item, filter);
    this.searchSubscription = this.searchValueChanges
      .pipe(
        debounceTime(this.searchDebounceMs),
        distinctUntilChanged(),
      )
      .subscribe((value: string) => this.applyFilterValue(value));
  }

  ngOnInit(): void {
    this.loadProducts();
  }

  ngAfterViewInit(): void {
    this.tableDataSource.sort = this.sort;
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }

  private async loadProducts(): Promise<void> {
    console.log('Loading products...');
    const response = await this.vendorProductsService.getAllVendorProductsV5();

    if (response.success) {
      this.tableDataSource.data = response.data;
      if (this.sort) this.tableDataSource.sort = this.sort;
    } else {
      console.error('Error loading products:', response.message || response.error);
    }
  }

  public addVendorProduct(): void {
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(
        this.toDialogVendorProductData(null),
        this.getDialogFields(false),
        'Vendor Product',
        true,
        'Add',
      ),
    });

    dialogRef.afterClosed().subscribe(async (dialogResult: Record<string, any> | null) => {
      if (!dialogResult) {
        return;
      }

      try {
        await this.vendorProductsService.postVendorProduct(this.buildVendorProductPayload(dialogResult));
        this.snackBar.open('Vendor product created', 'Close', { duration: 2000 });
        await this.loadProducts();
      } catch (error) {
        console.error('Error creating vendor product', error);
        this.snackBar.open('Error creating vendor product', 'Close', { duration: 2000 });
      }
    });
  }

  public async editVendorProduct(product: VendorProduct): Promise<void> {
    try {
      const productData = await this.vendorProductsService.getVendorProductById(product.id);
      const dialogRef = this.dialog.open(MemberAddeditComponent, {
        data: new MemberAddeditDialogModel(
          this.toDialogVendorProductData(productData),
          this.getDialogFields(true),
          'Vendor Product',
        ),
      });

      dialogRef.afterClosed().subscribe(async (dialogResult: Record<string, any> | null) => {
        if (!dialogResult) {
          return;
        }

        try {
          const updateResponse = await this.vendorProductsService.putVendorProduct(
            product.id,
            this.buildVendorProductPayload(dialogResult),
          );

          if (!updateResponse.success) {
            this.snackBar.open(
              updateResponse.message || updateResponse.error || 'Error saving vendor product',
              'Close',
              { duration: 3000 },
            );
            return;
          }

          this.snackBar.open('Vendor product updated', 'Close', { duration: 2000 });
          await this.loadProducts();
        } catch (error) {
          console.error('Error saving vendor product', error);
          this.snackBar.open('Error saving vendor product', 'Close', { duration: 2000 });
        }
      });
    } catch (error) {
      console.error('Error loading vendor product', error);
      this.snackBar.open('Error loading vendor product', 'Close', { duration: 2000 });
    }
  }

  public goToProduct(product: any): void {
    this.editVendorProduct(product);
  }

  public getMetadataPreview(product: VendorProduct): string {
    const metadataText = this.getMetadataText(product);
    if (metadataText.length <= this.metadataPreviewLength) {
      return metadataText;
    }
    return `${metadataText.slice(0, this.metadataPreviewLength - 3)}...`;
  }

  public getMetadataTooltip(product: VendorProduct): string {
    const metadataText = this.getMetadataText(product);
    return metadataText.length > this.metadataPreviewLength ? metadataText : '';
  }

  public queueFilter(value: string): void {
    this.searchValueChanges.next(value);
  }

  public clearSearch(): void {
    this.searchValue = '';
    this.queueFilter('');
  }

  async clone(event: Event, product: any): Promise<void> {
    event.stopPropagation();
    const clone: any = { ...product };
    clone.id = undefined;
    clone.productName = `${product.productName} - COPY`;
    clone.productSlug = product.productSlug ? `${product.productSlug}-copy` : undefined;
    try {
      await this.vendorProductsService.postVendorProduct(clone);
      this.snackBar.open('Vendor product cloned', 'Close', { duration: 2000 });
      this.loadProducts();
    } catch (error) {
      console.error('Error cloning vendor product', error);
      this.snackBar.open('Error cloning vendor product', 'Close', { duration: 2000 });
    }
  }

  async runFtp(event: Event, product: any, method: 'enroll' | 'payment'): Promise<void> {
    event.stopPropagation();
    try {
      await this.vendorProductsService.runFtp(product.id, method);
      this.snackBar.open(`Run ${method} FTP queued`, 'Close', { duration: 2000 });
    } catch (error) {
      console.error('Error running FTP', error);
      this.snackBar.open('Error running FTP', 'Close', { duration: 2000 });
    }
  }

  async rerunCarrierIntegration(event: Event, product: VendorProduct): Promise<void> {
    event.stopPropagation();
    const productLabel = product.productName || product.productSlug || product.id;
    const confirm = await this.uiAlertService.presentAlertConfirm(
      `Rerun carrier integration for vendor product ${productLabel}?`,
    );
    if (!confirm) {
      return;
    }

    try {
      const result = await this.vendorProductsService.rerunCarrierIntegration(
        product.id,
        'Admin requested from Vendor Products page',
      );
      const productCount = result?.vendorProductCount;
      const productText = typeof productCount === 'number'
        ? ` for ${productCount} vendor product${productCount === 1 ? '' : 's'}`
        : '';
      this.snackBar.open(`Carrier integration rerun queued${productText}`, 'Close', { duration: 3000 });
    } catch (error) {
      console.error('Error queueing carrier integration rerun', error);
      this.snackBar.open('Unable to queue carrier integration rerun', 'Close', { duration: 3000 });
    }
  }

  async deleteVendorProduct(event: Event, product: any): Promise<void> {
    event.stopPropagation();
    const confirm = await this.uiAlertService.presentAlertConfirm(`Delete vendor product ${product.productName}?`);
    if (!confirm) {
      return;
    }
    try {
      await this.vendorProductsService.deleteVendorProduct(product.id);
      this.snackBar.open('Vendor product deleted', 'Close', { duration: 2000 });
      this.loadProducts();
    } catch (error) {
      console.error('Error deleting vendor product', error);
      this.snackBar.open('Error deleting vendor product', 'Close', { duration: 2000 });
    }
  }

  private getDialogFields(includeId: boolean): Field[] {
    return includeId
      ? this.dialogFields
      : this.dialogFields.filter((field) => field.name !== 'id');
  }

  private getMetadataText(product: VendorProduct): string {
    if (!product?.metadata) {
      return '';
    }
    return JSON.stringify(product.metadata);
  }

  private matchesFilter(product: VendorProduct, filter: string): boolean {
    if (!filter) {
      return true;
    }

    const haystack = [
      product.vendorName,
      product.productName,
      product.vendorSlug,
      product.productSlug,
      product.stripeProductId,
      product.stripeVendor,
      this.getMetadataText(product),
    ]
      .filter((value): value is string => !!value)
      .join(' ')
      .toLowerCase();

    return haystack.includes(filter);
  }

  private applyFilterValue(value: string): void {
    this.tableDataSource.filter = value.trim().toLowerCase();
  }

  private toDialogVendorProductData(product: Partial<VendorProduct> | null): Record<string, any> {
    return structuredClone({
      id: product?.id || '',
      vendorName: product?.vendorName || '',
      productName: product?.productName || '',
      vendorSlug: product?.vendorSlug || '',
      productSlug: product?.productSlug || '',
      stripeProductId: product?.stripeProductId || '',
      stripeVendor: product?.stripeVendor || '',
      defaultBenefitCategory: getProductPayrollBenefitDefault(product?.metadata)?.category || 'none',
      defaultBenefitSubcategory: getProductPayrollBenefitDefault(product?.metadata)?.subcategory || 'none',
      metadata: product?.metadata || {},
      feedConfig: product?.feedConfig || {},
    });
  }

  private buildVendorProductPayload(dialogResult: Record<string, any>): Partial<VendorProduct> {
    const metadata = { ...(dialogResult['metadata'] || {}) };
    const defaultBenefit = getProductPayrollBenefitDefault({ defaultPayrollBenefit: {
      category: dialogResult['defaultBenefitCategory'],
      subcategory: dialogResult['defaultBenefitSubcategory'],
    } });
    if (defaultBenefit) {
      metadata.defaultPayrollBenefit = defaultBenefit;
    } else {
      delete metadata.defaultPayrollBenefit;
    }
    return {
      vendorName: dialogResult['vendorName']?.trim?.() || '',
      productName: dialogResult['productName']?.trim?.() || '',
      vendorSlug: dialogResult['vendorSlug']?.trim?.() || '',
      productSlug: dialogResult['productSlug']?.trim?.() || '',
      stripeProductId: dialogResult['stripeProductId']?.trim?.() || '',
      stripeVendor: dialogResult['stripeVendor']?.trim?.() || '',
      metadata,
      feedConfig: dialogResult['feedConfig'] || {},
    };
  }
}
